import React, { useState, useEffect } from 'react';
import { Table, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';

const IntentsGrid = ({ intents }) => {
  const [rowData, setRowData] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // Prepare Data to include children for tree structure
  const prepareData = (intents) => {
    return intents
      .filter(majorIntent => majorIntent.intentName.trim() !== '') // Filter out major intents with empty names
      .map((majorIntent) => ({
        key: majorIntent.intentName,
        intentName: majorIntent.intentName,
        totalCalls: majorIntent.totalCalls,
        category: majorIntent.category,
        ...(majorIntent.minorIntents.length > 0 && {
          children: majorIntent.minorIntents
            .filter(minorIntent => minorIntent.name.trim() !== '') // Filter out minor intents with empty names
            .map((minorIntent) => ({
              key: `${majorIntent.intentName}-${minorIntent.name}`,
              intentName: minorIntent.name,
              totalCalls: minorIntent.count,
              category: majorIntent.category,
            })),
        }),
      }))
      .filter(item => item.children ? item.children.length > 0 : true) // Remove major intents with no valid children
      .sort((a, b) => b.totalCalls - a.totalCalls); // Sort major intents by totalCalls in descending order
  };

  // Handle Intent Click to navigate to /intent/{intentName}
  const handleIntentClick = (intentName) => {
    navigate(`/intent/${encodeURIComponent(intentName)}`, { 
      state: { 
        referrer: location.pathname
      } 
    });
  };

  // Define table columns with clickable intent names
  const columns = [
    {
      title: 'Intent',
      dataIndex: 'intentName',
      key: 'intentName',
      align: 'left',
      render: (text, record) => {
        if (record.children) {
          // Major intent
          return (
            <div className="pl-6">
              <span className="bg-indigo-500 text-white text-xs font-medium mr-2 px-2.5 py-1 rounded-full cursor-pointer">
                {text}
              </span>
            </div>
          );
        } else {
          // Minor intent
          return (
            <div className="pl-20">
              <a className="text-blue-600 hover:underline" onClick={() => handleIntentClick(record.intentName)}>
                {text}
              </a>
            </div>
          );
        }
      },
    },
    {
      title: 'Total Calls',
      dataIndex: 'totalCalls',
      key: 'totalCalls',
      align: 'center',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      align: 'center',
      render: (text, record) => {
        if (record.children) {
          // Only show category for major intents
          return (
            <span className="bg-orange-100 text-orange-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full">
              {text}
            </span>
          );
        }
        return null;
      },
    },
  ];

  // Update rowData when intents prop changes
  useEffect(() => {
    if (intents && intents.length > 0) {
      const data = prepareData(intents);
      setRowData(data);
    }
  }, [intents]);

  // Filter Function to search and filter intents by minor intent name
  const filterIntentsByName = (searchTerm) => {
    if (!searchTerm || searchTerm.length < 3) {
      const data = prepareData(intents);
      setRowData(data);
      setExpandedRowKeys([]); // Collapse all
      return;
    }

    const filteredIntents = intents
      .map((majorIntent) => {
        const matchingMinorIntents = majorIntent.minorIntents.filter((minorIntent) =>
          minorIntent.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (matchingMinorIntents.length > 0) {
          return {
            ...majorIntent,
            minorIntents: matchingMinorIntents,
          };
        } else {
          return null;
        }
      })
      .filter(Boolean);

    const data = prepareData(filteredIntents);
    setRowData(data);

    // Expand all parent keys
    const keysToExpand = data.map((item) => item.key);
    setExpandedRowKeys(keysToExpand);
  };

  // Handle expand/collapse functionality
  const onExpand = (expanded, record) => {
    let keys = [];
    if (expanded) {
      keys = [...expandedRowKeys, record.key];
    } else {
      keys = expandedRowKeys.filter((k) => k !== record.key);
    }
    setExpandedRowKeys(keys);
  };

  return (
    <div>
      <div className="mb-6 max-w-md mx-auto">
        <Input
          placeholder="Search Minor Intents"
          prefix={<SearchOutlined className="text-gray-400" />}
          onChange={(e) => filterIntentsByName(e.target.value)}
          className="rounded-full py-2 px-4 border-2 border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition duration-300 ease-in-out"
          style={{
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        />
      </div>
      {rowData.length > 0 ? (
        <Table
          columns={columns}
          dataSource={rowData}
          expandable={{
            expandedRowKeys,
            onExpand,
          }}
          pagination={false}
          scroll={{x: '100%' }}
        />
      ) : (
        <p>No data available</p>
      )}
    </div>
  );
};


export default IntentsGrid;
